import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApp } from "@/context/AppContext";
import { api, fmt, monthLabel, downloadCsv } from "@/lib/api";
import { TransactionModal } from "@/components/TransactionModal";
import { CsvImportModal } from "@/components/CsvImportModal";
import { RecurringManager } from "@/components/RecurringManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CategoryOptions } from "@/components/TransactionModal";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, FileSpreadsheet, Trash2, Receipt, Search, Pencil, Download, Repeat } from "lucide-react";

export default function Transactions() {
  const { month } = useApp();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const { data: cats = [] } = useQuery({ queryKey: ["categories"], queryFn: api.getCategories });
  const { data: txns = [], isLoading } = useQuery({ queryKey: ["transactions", month], queryFn: () => api.getTransactions(month) });

  const catName = (id) => cats.find((c) => c.id === id)?.name;
  const catColor = (id) => cats.find((c) => c.id === id)?.color || "#a855f7";

  const deleteMut = useMutation({ mutationFn: api.deleteTransaction, onSuccess: () => { qc.invalidateQueries(); toast.success("Transaction deleted"); } });
  const updateMut = useMutation({ mutationFn: ({ id, data }) => api.updateTransaction(id, data), onSuccess: () => qc.invalidateQueries() });

  const filtered = txns.filter((t) => t.merchant.toLowerCase().includes(search.toLowerCase()));

  const exportCsv = () => {
    if (filtered.length === 0) return toast.error("Nothing to export");
    const header = ["Date", "Merchant", "Amount", "Category", "User", "Source", "Note"];
    const rows = [header, ...filtered.map((t) => [t.date, t.merchant, t.amount, catName(t.category_id) || "Unassigned", t.user, t.source, t.note || ""])];
    downloadCsv(`transactions-${month}.csv`, rows);
    toast.success(`Exported ${filtered.length} transactions`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Receipt className="w-6 h-6 text-[#f97316]" /> Transactions
          </h2>
          <p className="text-slate-400 text-sm mt-1">{filtered.length} entries · {monthLabel(month)}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="export-transactions-button" onClick={exportCsv} variant="outline" className="border-[#2a364f] text-slate-300 hover:bg-[#2a364f] hover:text-slate-100 gap-2"><Download className="w-4 h-4" /> Export</Button>
          <RecurringManager cats={cats} trigger={<Button data-testid="recurring-button" variant="outline" className="border-[#22c55e]/40 text-[#22c55e] hover:bg-[#22c55e]/10 hover:text-[#22c55e] gap-2"><Repeat className="w-4 h-4" /> Recurring</Button>} />
          <CsvImportModal cats={cats} trigger={<Button data-testid="csv-import-button" variant="outline" className="border-[#22c55e]/40 text-[#22c55e] hover:bg-[#22c55e]/10 hover:text-[#22c55e] gap-2"><FileSpreadsheet className="w-4 h-4" /> Import CSV</Button>} />
          <TransactionModal cats={cats} trigger={<Button data-testid="add-transaction-button" className="bg-[#f97316] hover:bg-[#ea580c] text-white gap-2"><Plus className="w-4 h-4" /> Add</Button>} />
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input data-testid="transaction-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search merchants…" className="pl-9 bg-[#1a2232] border-[#2a364f]" />
      </div>

      <div className="rounded-2xl bg-[#1a2232] border border-[#2a364f] overflow-hidden">
        {isLoading ? (
          <div className="py-12 text-center text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-slate-500">No transactions this month. Add one or import a CSV.</div>
        ) : (
          <div className="divide-y divide-[#2a364f]/60">
            {filtered.map((t) => (
              <div key={t.id} data-testid={`transaction-${t.id}`} className="flex items-center gap-4 px-4 sm:px-5 py-3.5 hover:bg-[#121824]/50 transition-colors">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center font-heading font-bold text-sm shrink-0" style={{ background: catColor(t.category_id) + "22", color: catColor(t.category_id) }}>
                  {t.merchant[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{t.merchant}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    <span className="font-mono-num">{t.date}</span>
                    <span>·</span>
                    <span>{t.user}</span>
                    {t.source === "csv" && <Badge variant="outline" className="h-4 px-1 text-[10px] border-[#22c55e]/40 text-[#22c55e]">CSV</Badge>}
                    {t.source === "recurring" && <Badge variant="outline" className="h-4 px-1 text-[10px] border-[#22c55e]/40 text-[#22c55e] gap-0.5"><Repeat className="w-2.5 h-2.5" /> Recurring</Badge>}
                  </div>
                </div>
                <div className="w-40 hidden sm:block">
                  <Select value={t.category_id || "__none__"} onValueChange={(v) => updateMut.mutate({ id: t.id, data: { category_id: v === "__none__" ? null : v } })}>
                    <SelectTrigger data-testid={`txn-category-${t.id}`} className="h-8 bg-[#0a0e14] border-[#2a364f] text-xs">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a2232] border-[#2a364f] text-slate-100 max-h-60">
                      <SelectItem value="__none__" className="text-[#a855f7]">Unassigned</SelectItem>
                      <CategoryOptions cats={cats} />
                    </SelectContent>
                  </Select>
                </div>
                <div className="font-mono-num font-semibold text-right w-24">{fmt(t.amount)}</div>
                <TransactionModal
                  cats={cats}
                  transaction={t}
                  trigger={
                    <Button variant="ghost" size="icon" data-testid={`edit-txn-${t.id}`} className="h-8 w-8 text-slate-500 hover:text-[#f97316] hover:bg-[#f97316]/10 shrink-0">
                      <Pencil className="w-4 h-4" />
                    </Button>
                  }
                />
                <Button variant="ghost" size="icon" data-testid={`delete-txn-${t.id}`} onClick={() => deleteMut.mutate(t.id)} className="h-8 w-8 text-slate-500 hover:text-red-400 hover:bg-red-500/10 shrink-0">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
